/*
 * ototo-ds-bestseller.js — the band's tabs, gallery, quantity, variant chips
 * and the parallax character field.
 *
 * It never builds markup and never holds a price: all six products are already
 * rendered by the store and this only decides which panel is visible. Without
 * it the first product shows with a real add-to-cart form, every thumbnail is a
 * real link to the product page, and the quantity input is a real number field.
 *
 * ⚠️ `hidden` is only a default — the CSS declares
 * [hidden]{display:none !important} against the panel's own class.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-ot-bs]');
  if (!root) return;

  var tabs = [].slice.call(root.querySelectorAll('[data-ot-bs-tab]'));
  var panels = [].slice.call(root.querySelectorAll('[data-ot-bs-panel]'));

  /* The buy column is capped to the screen, and on a short screen (a laptop at
     150% scaling shows ~600px) that cap fell ~170px short of the gallery
     beside it, leaving a blank strip under the column (team, 2026-10-12). The
     CSS lets the column grow to at least the gallery's height; this hands it
     that height, which CSS cannot read across grid columns. A hidden panel's
     gallery measures 0, and the observer fires again when it is shown. */
  if ('ResizeObserver' in window) {
    var galleryRO = new ResizeObserver(function (entries) {
      entries.forEach(function (en) {
        var p = en.target.closest('[data-ot-bs-panel]');
        if (p) p.style.setProperty('--ot-bs-gallery-h', Math.round(en.target.getBoundingClientRect().height) + 'px');
      });
    });
    panels.forEach(function (p) {
      var g = p.querySelector('.ot-bs__gallery');
      if (g) galleryRO.observe(g);
    });
  }

  function select(id, moveFocus) {
    tabs.forEach(function (t) { t.setAttribute('aria-selected', String(t.getAttribute('data-ot-bs-tab') === id)); });
    panels.forEach(function (p) {
      if (p.getAttribute('data-ot-bs-panel') === id) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    });
    var chosen = tabs.filter(function (t) { return t.getAttribute('data-ot-bs-tab') === id; })[0];
    if (moveFocus && chosen) chosen.focus();
    if (chosen) paintCharacter(chosen);
  }

  root.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-ot-bs-tab]');
    if (t) { select(t.getAttribute('data-ot-bs-tab'), false); return; }

    /* Gallery. The thumbnails are links to the product page; with the script
       running the click swaps the stage instead, which is what a shopper
       expects from a gallery and what they get on the PDP itself. */
    var stepBtn = e.target.closest && e.target.closest('[data-ot-bs-step]');
    if (stepBtn) {
      var sp = stepBtn.closest('[data-ot-bs-panel]');
      var thumbs = sp ? Array.prototype.slice.call(sp.querySelectorAll('[data-ot-bs-thumb]')) : [];
      if (thumbs.length < 2) return;
      var cur = thumbs.findIndex(function (a) { return a.classList.contains('is-active'); });
      var dir = parseInt(stepBtn.getAttribute('data-ot-bs-step'), 10);
      thumbs[(Math.max(cur, 0) + dir + thumbs.length) % thumbs.length].click();
      return;
    }

    var thumb = e.target.closest && e.target.closest('[data-ot-bs-thumb]');
    if (thumb) {
      var panel = thumb.closest('[data-ot-bs-panel]');
      var stage = panel && panel.querySelector('[data-ot-bs-stage] img');
      if (!stage) return;
      e.preventDefault();
      stage.removeAttribute('srcset');
      stage.removeAttribute('sizes');
      stage.src = thumb.getAttribute('data-ot-bs-thumb');
      panel.querySelectorAll('[data-ot-bs-thumb]').forEach(function (a) { a.classList.toggle('is-active', a === thumb); });
      return;
    }

    /* Quantity. */
    var step = e.target.closest && e.target.closest('[data-ot-qty]');
    if (step) {
      var input = step.parentNode.querySelector('input');
      if (!input) return;
      var n = parseInt(input.value, 10);
      if (isNaN(n)) n = 1;
      input.value = Math.max(1, n + parseInt(step.getAttribute('data-ot-qty'), 10));
      return;
    }

    /* Variant chips. The pressed chip always wins and every other row moves to
       match, so the shopper can never reach a dead end. */
    var chip = e.target.closest && e.target.closest('[data-ot-bs-chip]');
    if (!chip) return;
    var host = chip.closest('[data-ot-bs-panel]');
    var data = host && host.querySelector('[data-ot-bs-variants]');
    if (!data) return;
    var variants;
    try { variants = JSON.parse(data.textContent).variants; } catch (err) { return; }

    var pos = parseInt(chip.getAttribute('data-ot-bs-chip'), 10);
    var val = chip.getAttribute('data-ot-bs-chip-value');
    var current = [];
    host.querySelectorAll('[data-ot-bs-chip][aria-pressed="true"]').forEach(function (c) {
      current[parseInt(c.getAttribute('data-ot-bs-chip'), 10)] = c.getAttribute('data-ot-bs-chip-value');
    });
    current[pos] = val;

    var exact = variants.filter(function (v) {
      return current.every(function (want, i) { return want === undefined || v.options[i] === want; });
    })[0];
    var loose = variants.filter(function (v) { return v.options[pos] === val; })[0];
    var chosen = exact || loose;
    if (!chosen) return;

    var idField = host.querySelector('[data-ot-bs-id]');
    if (idField) idField.value = chosen.id;
    var price = host.querySelector('.ot-bs__price');
    if (price) price.childNodes[0].nodeValue = chosen.price;
    if (chosen.image) {
      var img = host.querySelector('[data-ot-bs-stage] img');
      if (img) { img.removeAttribute('srcset'); img.src = chosen.image; }
    }
    host.querySelectorAll('[data-ot-bs-color]').forEach(function (el) {
      var cv = chosen.options[parseInt(el.getAttribute('data-ot-bs-color'), 10)];
      if (cv && el.textContent !== cv) el.textContent = cv;
    });
    host.querySelectorAll('[data-ot-bs-chip]').forEach(function (c) {
      var p = parseInt(c.getAttribute('data-ot-bs-chip'), 10);
      c.setAttribute('aria-pressed', String(chosen.options[p] === c.getAttribute('data-ot-bs-chip-value')));
    });
  });

  /* ── the stars jump to the reviews ──────────────────────────────────────
     🔴 THE TARGET CANNOT BE RESOLVED ONCE, AT LOAD. The first version did, and
     demoted the button on every page: Yotpo's widget is injected by the app
     SECONDS after DOMContentLoaded, so at the moment this runs there is nothing
     to find and the control removed itself from the very page it belongs on.
     Measured — `isJump: false` on all three products tested.

     So: the click ALWAYS resolves fresh, and the demotion is what waits. The
     button is only stripped of its affordance once several delayed checks have
     all come back empty, which on the homepage band is the truth and on a PDP
     never happens.

     ⚠️ THE APP'S IDS ARE NOT STABLE AND MUST NOT BE HARDCODED. Yotpo's element
     ids on this store include `yotpo_stars_gradient_0.6939803009503526` — a
     random number minted per render — and the section id is
     `template--16601908838480__17352976866dcb55e9`, which carries the THEME id
     and changes the moment the theme is duplicated. Only the class names are
     stable, and the last resort is to read the sections looking for the
     widget's own words, which is what a person would do. */
  function reviewsTarget() {
    /* ⚠️ THE LAST MATCH, AND NEVER FILTERED BY HEIGHT. Two things had to be
       learned here, each of which sent the jump 69px and stopped.

       Yotpo puts SEVERAL widgets on a product page — the star rating beside the
       title answers to the same selectors as the review block and comes FIRST
       in document order, so `querySelector` handed back the rating the button
       is already sitting next to. The reviews are always the lower one.

       Then, filtering by height to tell them apart made it worse: the review
       widget is rendered LAZILY and measures ~0 tall until the viewport is near
       it, so at the top of the page a height test rejects the very element
       being looked for — and the button, finding nothing, demoted itself. A
       zero-height element still reports the right POSITION, which is all a jump
       needs. Position is reliable here; size is not. */
    var pool = [].slice.call(document.querySelectorAll(
      '.yotpo-reviews-main-widget, #yotpo-reviews-main-widget, [class*="yotpo-main-widget"], .yotpo-widget-instance'
    ));
    if (pool.length) return pool[pool.length - 1];
    var sections = [].slice.call(document.querySelectorAll('[id^="shopify-section"]'));
    for (var i = 0; i < sections.length; i++) {
      if (/customer reviews|based on [\d,.]+ reviews|write a review/i.test(sections[i].textContent || '')) return sections[i];
    }
    return null;
  }

  var jumps = [].slice.call(document.querySelectorAll('[data-ot-reviews-jump]'));
  if (jumps.length) {
    jumps.forEach(function (b) {
      b.addEventListener('click', function () {
        var t = reviewsTarget();
        if (!t) return; /* nothing to jump to — say nothing rather than lurch */
        /* ⚠️ THE HEADER IS STICKY, so scrollIntoView puts the widget's first
           line underneath it. Offset by the header's REAL height rather than a
           guessed number: it differs between the phone and the desktop bar, and
           it changes whenever the logo setting does. */
        var header = document.querySelector('.ot-header');
        var pad = header ? header.getBoundingClientRect().height + 12 : 12;
        /* ⚠️ `scrollIntoView` + `scroll-margin-top`, NOT `window.scrollTo` WITH A
           COMPUTED OFFSET. The absolute-offset version was measured landing at
           scrollY 69 on a 4911px document — the browser resolves the target
           itself here, so nothing depends on a number computed a frame before
           the app finishes laying the widget out. `scroll-margin-top` is what
           keeps the widget's first line out from under the sticky header, and
           it is set from the header's REAL height because that differs between
           the phone and the desktop bar and changes with the logo setting. */
        t.style.scrollMarginTop = Math.round(pad) + 'px';
        /* 🔴 ONE SCROLL IS NOT ENOUGH, AND THIS IS THE "SCROLL LOCK".
           From the top of this page the document is about 969px tall — every
           section below the fold is lazy — so the furthest anything can scroll
           is 69px, and that is exactly where a jump to a target 1600px down
           lands. It is not a clamp, a lock, or an app: the destination does not
           exist yet. The page then grows as the content it just revealed loads.
           CLAUDE.md records this as a testing trap ("go past ~3500"); it is not
           only a testing trap, it defeats any single-call anchor.

           So the scroll repeats until the target actually arrives under the
           header, or until it has clearly stopped moving. Instant, not smooth:
           a smooth scroll is abandoned by the browser on the next competing
           scroll, and on a page that is growing underneath there is always a
           next one. */
        /* 🔴 IT HAS TO WALK, NOT JUMP, AND THAT IS NOT A STYLE CHOICE.
           From the top of this page the document is about 969px tall — every
           section below the fold is lazy — so the furthest ANY scroll can reach
           is 69px, which is exactly where a jump to a target 1600px down lands.
           The page only grows as the content it reveals loads, and it only
           reveals content when you scroll: jump once and you deadlock at 69,
           because nothing further ever loads to scroll to. Repeating the same
           jump does not help for the same reason.

           Stepping down a screen at a time loads each band on the way and the
           document grows underneath, so the target becomes reachable. It stops
           the moment the widget is under the header, gives up if the page stops
           moving (nothing left to load), and lands precisely with one final
           `scrollIntoView` so `scroll-margin-top` does the last few pixels.

           CLAUDE.md records this as a testing trap — "scrollTo(0, 2000) lands at
           scrollY 69, go past ~3500". It is not only a testing trap. It defeats
           any single-call anchor on this page. */
        var steps = 0;
        var stuck = 0;
        (function walk() {
          var top = t.getBoundingClientRect().top;
          if (Math.abs(top - pad) < 8) { t.scrollIntoView({ block: 'start' }); return; }
          if (top < pad) { t.scrollIntoView({ block: 'start' }); return; }
          var before = window.pageYOffset;
          window.scrollTo(0, before + Math.round(window.innerHeight * 0.9));
          if (window.pageYOffset === before) stuck++; else stuck = 0;
          if (stuck > 2 || ++steps > 40) { t.scrollIntoView({ block: 'start' }); return; }
          window.setTimeout(walk, 90);
        })();
      });
    });

    /* Demote only after the app has had a fair chance. Three looks, then the
       affordance goes and the stars are the plain text they always were. */
    var looks = 0;
    var watch = window.setInterval(function () {
      looks++;
      if (reviewsTarget()) { window.clearInterval(watch); return; }
      if (looks < 4) return;
      window.clearInterval(watch);
      jumps.forEach(function (b) {
        b.removeAttribute('data-ot-reviews-jump');
        b.classList.remove('ot-stars--jump');
        var sr = b.querySelector('.ot-sr');
        if (sr) sr.textContent = sr.textContent.replace(/\s*—\s*jump to them\s*$/, '');
      });
    }, 1500);
  }

  /* ── the details dialog ──────────────────────────────────────────────────
     Open and close only. Focus, Escape and the backdrop are the browser's job
     once `showModal()` is used — `show()` would give a dialog with none of it,
     which is the usual way this ends up inaccessible. */
  root.addEventListener('click', function (e) {
    var opener = e.target.closest && e.target.closest('[data-ot-more]');
    if (opener) {
      var dlg = document.getElementById(opener.getAttribute('data-ot-more'));
      if (dlg && typeof dlg.showModal === 'function') { e.preventDefault(); dlg.showModal(); }
      return;
    }
    var closer = e.target.closest && e.target.closest('[data-ot-modal-close]');
    if (closer) {
      var own = closer.closest('dialog');
      if (own) own.close();
      return;
    }
    /* Clicking the backdrop closes it. The click lands on the dialog itself —
       the backdrop is not a node — so the test is whether the point was outside
       the dialog's own box. */
    var dialogEl = e.target.closest && e.target.closest('dialog.ot-modal');
    if (dialogEl && e.target === dialogEl) {
      var b = dialogEl.getBoundingClientRect();
      var outside = e.clientX < b.left || e.clientX > b.right || e.clientY < b.top || e.clientY > b.bottom;
      if (outside) dialogEl.close();
    }
  });

  root.addEventListener('keydown', function (e) {
    var tab = e.target.closest && e.target.closest('[data-ot-bs-tab]');
    if (!tab) return;
    var i = tabs.indexOf(tab);
    var next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    e.preventDefault();
    select(tabs[next].getAttribute('data-ot-bs-tab'), true);
  });

  /* ── the character ───────────────────────────────────────────────────────
     Picking a chip repaints the whole band: its background takes the
     character's own colour at FULL strength, the heading's ink flips to suit,
     the 28 background copies become that character, and the packshot replays
     the character's entrance. Ported from paintCharacter() / paintChips() /
     onScroll() in Storefront.dc.html.

     The contrast problem a dark band creates is solved structurally rather than
     by weakening the colour: the buy column sits on its own white card, so the
     only text left on the colour is the heading block. */
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var field = root.querySelector('[data-ot-bs-field]');
  var copies = field ? [].slice.call(field.querySelectorAll('[data-ot-p]')) : [];

  /* Each character moves the way its animal would. `p` runs about −1…1, where 0
     is the stage at the middle of the viewport. */
  var MOTION = {
    fly: function (p) { return 'translate(' + (p * 26).toFixed(1) + 'px,' + (p * -42).toFixed(1) + 'px) rotate(' + (p * 10).toFixed(1) + 'deg)'; },
    rise: function (p) { return 'translateY(' + (p * -46).toFixed(1) + 'px)'; },
    dive: function (p) { return 'translateY(' + (p * 52).toFixed(1) + 'px) scale(' + (1 - Math.abs(p) * 0.05).toFixed(3) + ')'; },
    sidle: function (p) { return 'translateX(' + (p * 72).toFixed(1) + 'px) rotate(' + (p * 5).toFixed(1) + 'deg)'; },
    wipe: function (p) { return 'translateX(' + (p * -58).toFixed(1) + 'px) rotate(' + (p * -8).toFixed(1) + 'deg)'; },
    trot: function (p) { return 'translateX(' + (p * 86).toFixed(1) + 'px)'; }
  };

  var activeChar = null;      // the character data of the selected chip
  function motionOf() {
    return (activeChar && MOTION[activeChar.motion]) || function () { return 'none'; };
  }

  function readChar(tab) {
    /* ⚠️ THERE MAY BE NO TAB AT ALL. sections/ds-pdp.liquid renders the same
       panel on a product page with no character tablist, and the init below
       ends with paintCharacter(tabs[0]) — undefined there. Without this guard
       that threw, and the thrown error took the gallery, the variant chips,
       the quantity stepper and the details dialog down with it, because they
       all live in this one IIFE. */
    if (!tab) return null;
    var icon = tab.getAttribute('data-ot-char-icon');
    if (!icon) return null;
    return {
      icon: icon,
      bg: tab.getAttribute('data-ot-char-bg'),
      dark: tab.getAttribute('data-ot-char-dark') === 'true',
      enter: tab.getAttribute('data-ot-char-enter'),
      idle: tab.getAttribute('data-ot-char-idle'),
      motion: tab.getAttribute('data-ot-char-motion')
    };
  }

  /* The chip squares. The artwork sits on the SQUARE, not on the chip, so the
     invert has to follow the square's own ground rather than the chip's pressed
     state — three of the six saturated colours are light, and keying off
     `pressed` alone put a white silhouette on a light tile and blanked whichever
     chip the shopper had just picked. */
  function paintChips() {
    tabs.forEach(function (tab) {
      var sq = tab.querySelector('[data-ot-sq]');
      if (!sq) return;
      var img = sq.querySelector('[data-ot-sqi]');
      var on = tab.getAttribute('aria-selected') === 'true';
      sq.style.background = (on ? sq.getAttribute('data-bgon') : sq.getAttribute('data-bg')) || 'transparent';
      if (!img) return;
      img.style.filter = on && sq.getAttribute('data-dark') === 'true' ? 'invert(1)' : 'none';
      var anim = sq.getAttribute('data-anim');
      img.style.animation = anim && !reduce ? anim : 'none';
    });
  }

  function paintCharacter(tab) {
    paintChips();
    var c = readChar(tab);
    activeChar = c;

    /* No character for this product: the band keeps its default and the field
       stays as it is rather than inventing artwork. Same rule as the hero. */
    if (!c) return;

    root.style.background = c.bg;
    /* Chip.jsx takes an `onDark` prop and flips the chip's polarity with it:
       the chip's own values are ink and paper, which on a black band make the
       SELECTED chip invisible and the five unselected ones read as chosen. */
    root.classList.toggle('ot-bs--dark', !!c.dark);
    var ink = c.dark ? '#fff' : 'var(--ot-ink)';
    var h = root.querySelector('[data-ot-bandink]');
    var eb = root.querySelector('[data-ot-bandeyebrow]');
    var sb = root.querySelector('[data-ot-bandsub]');
    if (h) h.style.color = ink;
    if (eb) eb.style.color = c.dark ? 'rgba(255,255,255,0.82)' : 'var(--ot-ink)';
    if (sb) sb.style.color = c.dark ? 'rgba(255,255,255,0.88)' : 'var(--ot-text-body)';

    copies.forEach(function (el, k) {
      var img = el.querySelector('[data-ot-pi]');
      var amb = el.querySelector('[data-ot-pa]');
      if (img && img.getAttribute('src') !== c.icon) img.setAttribute('src', c.icon);
      /* The silhouettes are black. On a dark band they go white or the whole
         field disappears into the colour. */
      if (img) img.style.filter = c.dark ? 'invert(1)' : 'none';
      if (reduce) {
        if (img) img.style.animation = 'none';
        if (amb) amb.style.animation = 'none';
        return;
      }
      /* Every copy runs the character's idle at its own speed, plus one of four
         ambient loops on the layer above — so 28 copies of one silhouette never
         fall into step with each other. */
      var speed = parseFloat(el.getAttribute('data-speed')) || 1;
      if (img) {
        var idle = (c.idle || '').split(' ');
        var dur = (parseFloat(idle[1]) || 2.4) * speed;
        img.style.animation = idle[0] + ' ' + dur.toFixed(2) + 's ease-in-out ' + (k * 0.11).toFixed(2) + 's infinite' + (k % 2 ? ' alternate-reverse' : '');
      }
      if (amb) {
        amb.style.animation = el.getAttribute('data-amb') + ' ' + (7 + (k % 5) * 2.6).toFixed(1) + 's ease-in-out ' + (k * 0.17).toFixed(2) + 's infinite' + (k % 3 ? '' : ' reverse');
      }
    });

    /* Replay the entrance on the visible packshot. Clearing first is required:
       without it the browser keeps the running animation and the new character
       simply appears. */
    var panel = root.querySelector('[data-ot-bs-panel]:not([hidden])');
    var shot = panel && panel.querySelector('.ot-bs__shot');
    if (shot && !reduce) {
      shot.style.animation = 'none';
      void shot.offsetWidth;
      shot.style.animation = c.enter;
    } else if (shot) {
      shot.style.animation = 'none';
    }
    paint();
  }

  /* ── scroll ──────────────────────────────────────────────────────────────
     The field is driven ENTIRELY by scroll and gets no keyframe entrance of its
     own: a filled animation would own `transform` and silently beat every
     parallax write. */
  /* Is the card pinned yet? Compare its top against its own sticky offset
     rather than a hardcoded number, so the value lives in the stylesheet. */
  var wasPinned = false;
  function syncLock() {
    var panel = root.querySelector('[data-ot-bs-panel]:not([hidden])');
    var detail = panel && panel.querySelector('[data-ot-panel]');
    if (!detail) return;
    if (window.innerWidth < 900) { detail.classList.remove('is-locked'); return; }
    /* The threshold is the bottom of the sticky site header, measured. Reading
       it from a custom property looked tidier and does not work: an unregistered
       custom property computes to its token stream, so `--ot-pin-top` came back
       as the literal string "calc(84px + 12px)" and parseFloat gave NaN — a gate
       that silently never closed. */
    var head = document.querySelector('.ot-header');
    var pinTop = (head ? head.getBoundingClientRect().height : 0) + 12;
    var pinned = panel.getBoundingClientRect().top <= pinTop + 1;

    /* Snap out the overshoot, once, on the transition. The page stops scrolling
       the moment the gate opens, so wherever the last gesture happened to land
       is where the card stays — and a fast scroll lands it well under the
       sticky header, hiding the universe badge for as long as the shopper reads.
       Correcting once puts the card exactly under the header instead. Guarded by
       `wasPinned` so it cannot re-fire and fight the page. */
    if (pinned && !wasPinned) {
      var overshoot = panel.getBoundingClientRect().top - pinTop;
      if (overshoot < -2) window.scrollBy(0, overshoot);
    }
    wasPinned = pinned;

    detail.classList.toggle('is-locked', !pinned);
    /* Rewind while locked, so coming back up the page returns the column to the
       price and the buy button rather than to wherever it was left. */
    if (!pinned && detail.scrollTop !== 0) detail.scrollTop = 0;
  }

  function paint() {
    var box = root.getBoundingClientRect();
    if (box.bottom < 0 || box.top > window.innerHeight) return;
    var mid = window.innerHeight / 2;
    var move = motionOf();

    var stage = root.querySelector('[data-ot-bs-panel]:not([hidden]) [data-ot-char-scroll]');
    if (stage) {
      var sr = stage.getBoundingClientRect();
      var sp = Math.max(-1, Math.min(1, (mid - (sr.top + sr.height / 2)) / mid));
      /* The zoom is composed in rather than set on the image: the <img> is
         running the character's idle, and a keyframe animating `transform`
         beats any static value written there. Read from the stylesheet so the
         number lives in one place. */
      var zoom = getComputedStyle(stage).getPropertyValue('--ot-stage-zoom').trim() || '1';
      stage.style.transform = move(sp) + ' scale(' + zoom + ')';
    }

    var bp = Math.max(-1, Math.min(1, (mid - (box.top + box.height / 2)) / mid));
    copies.forEach(function (el) {
      var depth = parseFloat(el.getAttribute('data-depth')) || 0.3;
      /* Each copy keeps its own resting angle and facing, so the field reads as
         a scatter of one character rather than 28 clones in lockstep. */
      var rot = parseFloat(el.getAttribute('data-rot')) || 0;
      var flip = el.getAttribute('data-flip') === '1' ? ' scaleX(-1)' : '';
      el.style.transform = move(bp * depth * 2.4) + ' rotate(' + rot + 'deg)' + flip;
    });
  }

  /* ── the wheel ───────────────────────────────────────────────────────────
     The right column is what scrolls here, but the left half is a big still
     image: putting the pointer over it and turning the wheel should not feel
     like the section is dead. So a wheel anywhere in the band drives the panel
     — and hands the gesture BACK to the page the moment the panel reaches an
     end, which is what stops it trapping the scroll. */
  root.addEventListener('wheel', function (e) {
    if (window.innerWidth < 900) return;
    /* A sideways gesture belongs to whatever horizontal rail is under the pointer
       (the reels, the "goes with this" row) — never to this vertical logic. */
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    var panel = root.querySelector('[data-ot-bs-panel]:not([hidden]) [data-ot-panel]');
    if (!panel) return;

    /* ⚠️ While the column is locked the page is moved EXPLICITLY, rather than
       leaving the wheel to chain out of the panel on its own. Relying on the
       chain does not work: the column is still a scroll container with
       scrollable overflow, so the wheel is consumed by it and the whole
       right-hand half of the band became a dead zone — the page would not move
       at all with the pointer over it. `overscroll-behavior: auto` did not fix
       it either. Driving the scroll here is the only version that measures
       correctly. */
    if (panel.classList.contains('is-locked')) {
      e.preventDefault();
      window.scrollBy(0, e.deltaY);
      return;
    }

    /* Pinned. A wheel anywhere in the band drives the buy column — INCLUDING when
       the pointer is over the column itself, which used to be handed to native
       scroll and dead-ended at the bottom because the column's own
       `overscroll-behavior` swallowed the last turn. Drive it here, and the
       moment the column is already at the end it is being pushed toward, hand the
       wheel to the PAGE so the next (or previous) section scrolls in. */
    var max = panel.scrollHeight - panel.clientHeight;
    var atEnd = max <= 0
      || (e.deltaY < 0 && panel.scrollTop <= 0)
      || (e.deltaY > 0 && panel.scrollTop >= max - 1);
    e.preventDefault();
    if (atEnd) {
      window.scrollBy(0, e.deltaY);
    } else {
      panel.scrollTop = Math.max(0, Math.min(max, panel.scrollTop + e.deltaY));
    }
  }, { passive: false });

  var ticking = false;
  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      syncLock();
      if (!reduce) paint();
    });
  }, { passive: true });
  window.addEventListener('resize', function () { syncLock(); paint(); });
  syncLock();

  paintCharacter(tabs.filter(function (t) { return t.getAttribute('aria-selected') === 'true'; })[0] || tabs[0]);

  /* Idle cost (go-live 8.3, 2026-10-13): the character field runs ~60 looping
     animations. Pause every one of them while the band is off screen; they
     resume where they were when it scrolls back in. */
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { root.classList.toggle('ot-anim-paused', !en.isIntersecting); });
    }, { rootMargin: '200px 0px' }).observe(root);
  }
})();
